import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MainLayout from "../../../layouts/MainLayout";
import Button from "../../../components/Button";
import "../../../assets/styles/custom-styles.css";
import {
  faTrash,
  faUndo,
  faCircleChevronLeft,
  faCircleChevronRight,
  faCircleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import ConfirmationModal from "../../../components/modals/ConfirmationModal";
import { usePermissions } from "../../../hooks/usePermissions";
import useLoader from "../../../hooks/useLoader";
import { detailsMovementFields } from "../../../constants/forms/movementFields";
import DetailsSectionRenderer from "../../../components/DetailsSectionRenderer";
import useForm from "../../../hooks/useForm";
import { setDefaultFieldValues } from "../../../utils/objectUtils";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import PageHeader from "../../../components/PageHeader";
import ListHeader from "../../../components/ListHeader";
import useAction from "../../../hooks/useAction";
import { formatDateToInput } from "../../../utils/formatDateToInput";
import useUrlFilters from "../../../hooks/useUrlFilters";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useNotification from "../../../hooks/useNotification";
import { transformFiltersForUrl } from "../../../utils/transformFiltersForUrl";
import useFilterHandlers from "../../../hooks/useFilterHandlers";
import useGenericFilter from "../../../hooks/filters/useGenericFilter";
import TableWithInactiveFilter from "../../../components/table/TableWithInactiveFilter";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import MovementEnum from "../../../enums/movement-enum";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";
import { genericStatusColorMap } from "../../../utils/statusColorMap";
import {
  GenericStatusEnum,
  GenericStatusEnumById,
} from "../../../enums/generic-status-enum";
import { ShipmentItemStatus } from "../../../enums/shipmentItemStatus";
import { mapMovementDataToVolumeForm } from "./utils/mapMovementDataToVolumeForm";
import { EditVolumeModal } from "./components/volumes/EditVolumeModal";

const mapVolumeItem = (item) => {
  const isProduct = !!item.product_item_id;

  return {
    id: item.id,
    movement_item_id: item.movement_item_id,
    type: isProduct ? "product" : "equipment",
    equipment_id: item.equipment_id ?? null,
    product_item_id: item.product_item_id ?? null,
    product_id: item.product_item?.product_id ?? null,
    name: isProduct
      ? `${item.product_item?.product?.name} | ${item.product_item?.category?.name} | ${item.product_item?.condition?.name}`
      : `${item.equipment?.number} - ${item.equipment?.name}`,
    address: isProduct
      ? `${item.product_item?.address?.addressable?.alias} - ${item.product_item?.address?.formatted_address}`
      : `${item.equipment?.product_item?.address?.addressable?.alias} - ${item.equipment?.product_item?.address?.formatted_address}`,
    location: isProduct
      ? item.product_item?.location?.formatted_location
      : item.equipment?.product_item?.location?.formatted_location,
    quantity: item.quantity ?? 1,
    ...(item.equipment && { equipment_number: item.equipment?.number }),
    ...(item.equipment && { equipment_name: item.equipment?.name }),
    ...(item.product_item?.product && {
      product_name: item.product_item?.product?.name,
    }),
    ...(item.product_item?.category && {
      product_category: item.product_item?.category?.name,
    }),
    ...(item.product_item?.condition && {
      product_condition: item.product_item?.condition?.name,
    }),
  };
};

const mapVolumeRecord = (item) => ({
  id: item.id,
  code: item.code,
  name: item.name,
  shipped: item.shipped ? "Entregue" : "Aguardando Carregamento",
  status: GenericStatusEnumById[item.status] ?? "-",
  hasShipment: !!item.shipment,
  shipmentId: item.shipment?.id ?? null,
  created_at: item.created_at,
  items: Array.isArray(item.items) ? item.items.map(mapVolumeItem) : [],
  itemsLoaded: Array.isArray(item.items),
});

const DetailsMovementPage = () => {
  const navigate = useNavigate();
  const { movementId } = useParams();
  const queryClient = useQueryClient();

  const { canAccess } = usePermissions();
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const {
    getByColumn: baseServiceGetByColumn,
    get: baseServiceGet,
    put: baseServicePut,
    del: baseServiceDelete,
  } = useBaseService(navigate);

  const [equipColumns, setEquipColumns] = useState([]);
  const [equipmentsCollapseConfig, setEquipmentsCollapseConfig] = useState({});

  // useForm para os detalhes do movimento principal
  const { formData, setFormData, formatData } = useForm(
    setDefaultFieldValues(detailsMovementFields),
  );

  // --- Padronização de filtros via useUrlFilters para os itens do movimento ---
  const {
    filters,
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters(
    {
      deleted_at: false,
    },
    `movementItemsEquipments_${movementId}`,
  );

  const { handleFilterApply, handleFilterClear } =
    useFilterHandlers(applyUrlFilters);

  const { localFilterValues } = useGenericFilter(
    {
      deleted_at: false,
    },
    handleFilterApply,
    handleFilterClear,
  );

  const {
    filters: filtersProducts,
    setFilters: applyUrlFiltersProducts,
    pagination: paginationProducts,
    changePage: changePageProducts,
    setPagination: setPaginationProducts,
  } = useUrlFilters(
    {
      deleted_at: false,
    },
    `movementItemsProducts_${movementId}`,
  );

  const {
    handleFilterApply: handleFilterApplyProducts,
    handleFilterClear: handleFilterClearProducts,
  } = useFilterHandlers(applyUrlFiltersProducts);

  const { localFilterValues: localFilterValuesProducts } = useGenericFilter(
    {
      deleted_at: false,
    },
    handleFilterApplyProducts,
    handleFilterClearProducts,
  );

  const {
    setFilters: applyUrlFiltersVolumes,
    pagination: paginationVolumes,
    changePage: changePageVolumes,
    setPagination: setPaginationVolumes,
  } = useUrlFilters({}, `movementVolumes_${movementId}`);

  const {
    handleFilterApply: handleFilterApplyVolumes,
    handleFilterClear: handleFilterClearVolumes,
  } = useFilterHandlers(applyUrlFiltersVolumes);

  const { localFilterValues: localFilterValuesVolumes } = useGenericFilter(
    {},
    handleFilterApplyVolumes,
    handleFilterClearVolumes,
  );

  // Hook useAction para gerenciar ações de exclusão/ativação com modal de confirmação
  const {
    openModalConfirmation,
    action,
    showConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
  } = useAction(navigate);

  // --- Lógica de busca dos detalhes do movimento principal com TanStack Query ---
  const {
    data: movementDetailsResponseData,
    isLoading: isLoadingMovementDetails,
    isError: isErrorMovementDetails,
    error: movementDetailsQueryError,
  } = useQueryWithLoader({
    queryKey: ["movementDetails", movementId],
    queryFn: async () => {
      showLoader();
      const response = await baseServiceGetByColumn(
        entities.movements.getById(movementId),
      );
      return response.result;
    },
    enabled: !!movementId,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  // Efeito para exibir notificações de erro ao buscar detalhes do movimento
  useEffect(() => {
    if (
      !isLoadingMovementDetails &&
      isErrorMovementDetails &&
      movementDetailsQueryError
    ) {
      console.error(
        "DetailsMovementPage: Erro ao carregar detalhes do movimento (useQuery):",
        movementDetailsQueryError,
      );
      showNotification(
        "error",
        `Erro ao carregar detalhes do movimento: ${
          movementDetailsQueryError.response?.data?.message ||
          movementDetailsQueryError.message
        }`,
      );
    }
  }, [
    isLoadingMovementDetails,
    isErrorMovementDetails,
    movementDetailsQueryError,
    showNotification,
  ]);

  // Efeito para formatar e definir os dados do formulário quando a resposta da query estiver disponível
  useEffect(() => {
    if (
      !isLoadingMovementDetails &&
      !isErrorMovementDetails &&
      !movementDetailsQueryError &&
      movementDetailsResponseData
    ) {
      hideLoader();
      // Formata os dados principais do movimento para o formulário
      formatData(movementDetailsResponseData, detailsMovementFields);

      // Ajusta o formData com nomes amigáveis para exibição ou dados adicionais necessários
      setFormData((prev) => ({
        ...prev,
        movement_id: movementDetailsResponseData.id,
        number: movementDetailsResponseData.number || "-",
        recipient_customer:
          `${movementDetailsResponseData.recipient_customer_id} - ${movementDetailsResponseData.recipient_customer_name}` ||
          "-",
        destination: movementDetailsResponseData?.destination?.alias || "-",
        organization_id:
          movementDetailsResponseData.organization_name || prev.organization_id,
        movement_date: formatDateToInput(
          movementDetailsResponseData.movement_date,
        ),
        movement_type_id: movementDetailsResponseData.movement_type?.id || "",
        movement_type: movementDetailsResponseData.movement_type?.name || "",
        status_id: movementDetailsResponseData.status_name || prev.status_id,
        service_order_label:
          movementDetailsResponseData.service_order_id || prev.service_order_id
            ? `#${
                movementDetailsResponseData.service_order_id ||
                prev.service_order_id
              } - ${movementDetailsResponseData.movement_type?.name}`
            : movementDetailsResponseData.movement_type?.name,
        service_order_id:
          movementDetailsResponseData.service_order_id ||
          prev.service_order_id ||
          movementDetailsResponseData.separate_order?.id,
        created_at: movementDetailsResponseData.created_at
          ? formatDateToInput(movementDetailsResponseData.created_at)
          : movementDetailsResponseData.created_at,
      }));
    }
  }, [
    isLoadingMovementDetails,
    isErrorMovementDetails,
    movementDetailsQueryError,
    movementDetailsResponseData,
    hideLoader,
    formatData,
    setFormData,
  ]);

  // --- Lógica de busca dos ITENS do movimento com TanStack Query ---
  const {
    data: movementItemEquipmentsResponseData,
    isLoading: isLoadingMovementItemEquipments,
    isFetching: isFetchingMovementItemEquipments,
    isError: isErrorMovementItemEquipments,
    error: movementItemEquipmentsQueryError,
  } = useQueryWithLoader({
    queryKey: ["movementItemEquipments", movementId, filters, pagination],
    queryFn: async () => {
      const filtersTranformed = transformFiltersForUrl(filters);
      const params = {
        filters: filtersTranformed,
        page: pagination.page,
        perPage: pagination.perPage,
        lean: true,
      };
      const response = await baseServiceGet(
        entities.movements.itemEquipments.get(movementId),
        params,
      );
      return response.result;
    },
    keepPreviousData: true, // Mantém dados anteriores enquanto busca novos
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    select: (data) => {
      return {
        movementsEquipments: data.data
          .map((item) => ({
            id: item.id,
            equipment_id: item.equipment?.id,
            equipment_number: String(item.equipment?.number ?? "-"),
            equipment_name: item.equipment?.name ?? "-",
            extra_id: item.extra_id ?? null,
            equipment: item.extra_id
              ? (`(KIT) ${item.equipment?.number} - ${item.equipment?.name}` ??
                "-")
              : (`${item.equipment?.number} - ${item.equipment?.name}` ?? "-"),
            contract_item: item.order_item?.contract_item?.alias ?? "-",
            movement_type: item.type?.name ?? "-",
            status: GenericStatusEnumById[item.status_id] ?? "-",
            isDeleted: !!item.deleted_at,
            return_equipment: [
              {
                number: item.order_item?.equipment?.number ?? "-",
                name: item.order_item?.equipment?.name ?? "-",
                status:
                  GenericStatusEnumById[item.order_item?.status_id] ?? "-",
                contract_item: item.order_item?.contract_item?.alias ?? "-",
                shipment_item_status: item.shipment_item?.status_id ?? false,
              },
            ],
          }))
          .sort((a, b) => {
            if (!!a.extra_id !== !!b.extra_id) {
              return a.extra_id ? 1 : -1;
            }

            const nameCompare = a?.equipment_name?.localeCompare(
              b?.equipment_name,
            );
            if (nameCompare !== 0) return nameCompare;

            return a?.equipment_number?.localeCompare(
              b?.equipment_number,
              undefined,
              { numeric: true },
            );
          }),
        totalPages: data.last_page,
      };
    },
  });

  const { movementsEquipments = [], totalPages = 1 } =
    movementItemEquipmentsResponseData || {};

  const {
    data: movementItemProductsResponseData,
    isLoading: isLoadingMovementItemProducts,
    isFetching: isFetchingMovementItemProducts,
    isError: isErrorMovementItemProducts,
    error: movementItemProductsQueryError,
  } = useQueryWithLoader({
    queryKey: [
      "movementItemProducts",
      movementId,
      filtersProducts,
      paginationProducts,
    ],
    queryFn: async () => {
      const filtersTranformed = transformFiltersForUrl(filtersProducts);
      const params = {
        filters: filtersTranformed,
        page: paginationProducts.page,
        perPage: paginationProducts.perPage,
      };
      const response = await baseServiceGet(
        entities.movements.itemProducts.get(movementId),
        params,
      );
      return response.result;
    },
    keepPreviousData: true, // Mantém dados anteriores enquanto busca novos
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    select: (data) => {
      return {
        movementsProducts: data.data
          .map((item) => {
            const shippedQuantity =
              item.movement?.volumes?.reduce((acc, volume) => {
                // Filtramos os itens do volume que pertencem a este produto e estão em um carregamento
                const itemsInShipment =
                  volume.items?.filter(
                    (vItem) =>
                      vItem.product_item_id === item.product_item_id &&
                      vItem.shipment_item_id !== null,
                  ) || [];

                // Somamos a quantidade desses itens (usando a propriedade correta do objeto vItem)
                // Nota: Verifique se no seu objeto vItem o nome é 'quantity'. No JSON original parecia ser 1 item por entrada ou 'total_product_quantity'
                const volumeSum = itemsInShipment.reduce((sum, vItem) => {
                  return sum + (parseFloat(vItem.quantity) || 1);
                }, 0);

                return acc + volumeSum;
              }, 0) || 0;

            return {
              id: item.id,
              product_item_id: item.product_item?.id,
              product:
                `${item.product_item?.product?.name} | ${item.product_item?.category?.name} | ${item.product_item?.condition?.name}` ??
                "-",
              address:
                `${item.product_item?.address?.addressable?.alias} - ${item.product_item?.address?.formatted_address}` ??
                "-",
              location: item.product_item?.location?.formatted_location ?? "-",
              quantity_product: item.total_product_quantity ?? 0,
              progress_label: `${shippedQuantity ?? "-"} / ${item.total_product_quantity ?? "-"}`,
              movement_type: item.type?.name ?? "-",
              status: GenericStatusEnumById[item.status?.id] ?? "-",
              isDeleted: !!item.deleted_at,
            };
          })
          .sort((a, b) => a.product.localeCompare(b.product)),
        totalPages: data.last_page,
      };
    },
  });

  const { movementsProducts = [], totalPagesProducts = 1 } =
    movementItemProductsResponseData || {};

  const productsColumns = [
    { key: "product", label: "Insumo" },
    { key: "address", label: "Endereço" },
    { key: "location", label: "Localização" },
    { key: "progress_label", label: "Progresso (Carregados)", align: "center" },
    { key: "status", label: "Status" },
  ];

  const {
    data: movementVolumesResponseData,
    isLoading: isLoadingMovementVolumes,
    isFetching: isFetchingMovementVolumes,
  } = useQueryWithLoader({
    queryKey: ["movementVolumes", movementId, paginationVolumes],
    queryFn: async () => {
      const params = {
        page: paginationVolumes.page,
        perPage: paginationVolumes.perPage,
        sort: { column: "id", order: "desc" },
        view: "lean",
      };
      const response = await baseServiceGet(
        entities.movements.volumes.get(movementId),
        params,
      );
      return response.result;
    },
    keepPreviousData: true,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    select: (data) => {
      return {
        movementsVolumes: (data?.data ?? []).map(mapVolumeRecord),
        totalPages: data.last_page,
      };
    },
  });

  const { movementsVolumes = [], totalPagesVolumes = 1 } =
    movementVolumesResponseData || {};

  const [volumeItemsState, setVolumeItemsState] = useState({});
  const volumeItemsRequestsRef = useRef(new Set());

  const volumesTableData = useMemo(
    () =>
      movementsVolumes.map((volume) => {
        const loadedState = volumeItemsState[volume.id];

        if (!loadedState) return volume;

        return {
          ...volume,
          items: loadedState.items,
          itemsLoading: loadedState.loading,
          itemsLoaded: !loadedState.loading,
        };
      }),
    [movementsVolumes, volumeItemsState],
  );

  // Efeito para exibir notificações de erro ao buscar itens do movimento
  useEffect(() => {
    if (!isLoadingMovementItemEquipments) {
      hideLoader();
    }
    if (
      !isLoadingMovementItemEquipments &&
      isErrorMovementItemEquipments &&
      movementItemEquipmentsQueryError
    ) {
      console.error(
        "DetailsMovementPage: Erro ao carregar itens do movimento (useQuery):",
        movementItemEquipmentsQueryError,
      );
      showNotification(
        "error",
        `Erro ao carregar itens do movimento: ${
          movementItemEquipmentsQueryError.response?.data?.message ||
          movementItemEquipmentsQueryError.message
        }`,
      );
    }

    if (!isLoadingMovementItemProducts) {
      hideLoader();
    }
    if (
      !isLoadingMovementItemProducts &&
      isErrorMovementItemProducts &&
      movementItemProductsQueryError
    ) {
      console.error(
        "DetailsMovementPage: Erro ao carregar itens do movimento (useQuery):",
        movementItemProductsQueryError,
      );
      showNotification(
        "error",
        `Erro ao carregar itens do movimento: ${
          movementItemProductsQueryError.response?.data?.message ||
          movementItemProductsQueryError.message
        }`,
      );
    }
  }, [
    isLoadingMovementItemEquipments,
    isErrorMovementItemEquipments,
    movementItemEquipmentsQueryError,
    isLoadingMovementItemProducts,
    isErrorMovementItemProducts,
    movementItemProductsQueryError,
    showNotification,
    hideLoader,
  ]);

  // --- Mutações para Inativar e Ativar Itens do Movimento ---

  // Mutação para INATIVAR (soft delete) item do movimento
  const inactivateMovementItemMutation = useMutation({
    mutationFn: async (itemIdToInactivate) => {
      showLoader();
      const response = await baseServiceDelete(
        entities.movements.items.delete(movementId, itemIdToInactivate),
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        // Invalida MovementPage
        queryClient.invalidateQueries({ queryKey: ["movements"] });
        // Invalida DetailsMovementPage
        queryClient.invalidateQueries({ queryKey: ["movementItemEquipments"] });
        queryClient.invalidateQueries({ queryKey: ["movementItemProducts"] });
        queryClient.invalidateQueries({ queryKey: ["movementVolumes"] });
        queryClient.invalidateQueries({
          queryKey: ["movementDetails"],
        });
        queryClient.invalidateQueries({
          queryKey: ["osDetails"],
        });
        // Invalida CreateMovementPage
        queryClient.invalidateQueries({ queryKey: ["ordersServices"] });
        queryClient.invalidateQueries({ queryKey: ["serviceOrderItems"] });
        queryClient.invalidateQueries({
          queryKey: ["contractItemsForMovement"],
        });
      }
    },
    onError: (err) => {
      console.error("onError ", err);
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
      handleCancelConfirmation(); // Fecha o modal de confirmação
    },
  });

  // Mutação para ATIVAR item do movimento
  const activateMovementItemMutation = inactivateMovementItemMutation;

  // Define as ações da tabela, memoizadas para performance e com lógica de permissão/visibilidade
  const actions = useMemo(
    () => [
      {
        id: "delete",
        icon: faTrash,
        title: "Excluir",
        buttonClass: "btn-danger",
        permission: "Excluir itens de movimentos",
        onClick: (movementItem) => {
          showConfirmation(
            movementItem,
            `Você tem certeza que deseja excluir?`,
            async () => {
              try {
                await inactivateMovementItemMutation.mutateAsync(
                  movementItem.id,
                );
              } catch (error) {
                console.error("Erro ao excluir item do movimento:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          );
        },
        show: (movementItem) =>
          !movementItem.isDeleted &&
          movementItem.status === "NÃO ATENDIDO" &&
          canAccess("Excluir itens de movimentos"),
      },
      {
        id: "activate",
        icon: faUndo,
        title: "Restaurar",
        buttonClass: "btn-success",
        permission: "Excluir itens de movimentos",
        onClick: (movementItem) => {
          showConfirmation(
            movementItem,
            `Você tem certeza que deseja restaurar?`,
            async () => {
              try {
                await activateMovementItemMutation.mutateAsync(movementItem.id);
              } catch (error) {
                console.error("Erro ao restaurar item do movimento:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          );
        },
        show: (movementItem) =>
          movementItem.isDeleted && canAccess("Excluir itens de movimentos"),
      },
    ],
    [
      canAccess,
      showConfirmation,
      inactivateMovementItemMutation,
      activateMovementItemMutation,
    ],
  );

  const volumeDeleteMutation = useMutation({
    mutationFn: (volumeId) =>
      baseServiceDelete(
        entities.movements.volumes.delete(movementId, volumeId),
      ),
    onSuccess: (response) => {
      if (response?.status !== 200) return;

      setVolumeItemsState({});

      // Invalida MovementPage
      queryClient.invalidateQueries({ queryKey: ["movements"] });
      // Invalida DetailsMovementPage
      queryClient.invalidateQueries({ queryKey: ["movementItemEquipments"] });
      queryClient.invalidateQueries({ queryKey: ["movementItemProducts"] });
      queryClient.invalidateQueries({ queryKey: ["movementVolumes"] });
      queryClient.invalidateQueries({
        queryKey: ["movementDetails"],
      });
      queryClient.invalidateQueries({
        queryKey: ["osDetails"],
      });
    },

    onError: (error) => {
      console.error("onError ", error);
      hideLoader();
    },
  });

  const actionsVolume = useMemo(
    () => [
      {
        id: "delete",
        icon: faTrash,
        title: "Excluir",
        buttonClass: "btn-danger",
        permission: "Excluir volumes",
        onClick: (volume) => {
          showConfirmation(
            volume,
            `Você tem certeza que deseja deletar o volume`,
            async () => {
              try {
                await volumeDeleteMutation.mutateAsync(volume.id);
              } catch (error) {
                console.error("Erro ao deletar volume:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          );
        },
        show: (volume) =>
          volume.status === "NÃO ATENDIDO" && canAccess("Excluir volumes"),
      },
    ],
    [canAccess, showConfirmation, volumeDeleteMutation],
  );

  // mutação para deletar item do volume
  const volumeItemDeleteMutation = useMutation({
    mutationFn: ({ volumeId, itemId }) => {
      return baseServiceDelete(
        entities.movements.volumeItemDelete(movementId, volumeId, itemId),
      );
    },
    onSuccess: (response, variables) => {
      if (response?.status !== 200) return;

      setVolumeItemsState((prev) => {
        const current = prev[variables.volumeId];

        if (!current) return prev;

        return {
          ...prev,
          [variables.volumeId]: {
            ...current,
            items: current.items.filter((item) => item.id !== variables.itemId),
          },
        };
      });

      // Invalida MovementPage
      queryClient.invalidateQueries({ queryKey: ["movements"] });
      // Invalida DetailsMovementPage
      queryClient.invalidateQueries({ queryKey: ["movementItemEquipments"] });
      queryClient.invalidateQueries({ queryKey: ["movementItemProducts"] });
      queryClient.invalidateQueries({ queryKey: ["movementVolumes"] });
      queryClient.invalidateQueries({
        queryKey: ["movementDetails"],
      });
      queryClient.invalidateQueries({
        queryKey: ["osDetails"],
      });
    },

    onError: (error) => {
      console.error("onError ", error);
      hideLoader();
    },
  });

  const handleDeleteVolumeItem = (volume, item) => {
    if (volume.hasShipment) {
      showNotification(
        "warning",
        `Este volume está vinculado a um carregamento (ID #${volume.shipmentId}). Remova o volume do carregamento antes de editar os itens.`,
      );
      return;
    }

    showConfirmation(
      volume,
      "Deseja realmente deletar este item?",
      async () => {
        try {
          await volumeItemDeleteMutation.mutateAsync({
            volumeId: volume.id,
            itemId: item.id,
          });
        } catch (error) {
          console.error("Erro ao deletar item do volume:", error);
        }
      },
    );
  };

  const updateVolumeFromMovement = useMutation({
    mutationFn: async (data) => {
      showLoader();
      const response = await baseServicePut(
        entities.movements.volumesUpdate(movementId),
        data,
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        hideLoader();
        setIsEditVolumeModalOpen(false);
        setVolumeItemsState({});

        // Invalida MovementPage
        queryClient.invalidateQueries({ queryKey: ["movements"] });
        // Invalida DetailsMovementPage
        queryClient.invalidateQueries({ queryKey: ["movementItemEquipments"] });
        queryClient.invalidateQueries({ queryKey: ["movementItemProducts"] });
        queryClient.invalidateQueries({ queryKey: ["movementVolumes"] });
        queryClient.invalidateQueries({
          queryKey: ["movementDetails"],
        });
        queryClient.invalidateQueries({
          queryKey: ["osDetails"],
        });
      }
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const handleLoadVolumeItems = async (volume) => {
    if (volume.itemsLoaded || volumeItemsRequestsRef.current.has(volume.id)) {
      return;
    }

    volumeItemsRequestsRef.current.add(volume.id);
    setVolumeItemsState((prev) => ({
      ...prev,
      [volume.id]: { items: [], loading: true },
    }));

    try {
      const response = await baseServiceGet(
        entities.movements.volumes.get(movementId),
        {
          filters: { id: { $eq: volume.id } },
          page: 1,
          perPage: 1,
          sort: { column: "id", order: "desc" },
          lean: true,
        },
      );
      const volumeData = response?.result?.data?.[0];
      const items = volumeData ? mapVolumeRecord(volumeData).items : [];

      setVolumeItemsState((prev) => ({
        ...prev,
        [volume.id]: { items, loading: false },
      }));
    } catch (error) {
      console.error("Erro ao carregar itens do volume:", error);
      setVolumeItemsState((prev) => ({
        ...prev,
        [volume.id]: { items: [], loading: false },
      }));
      showNotification(
        "error",
        "Não foi possível carregar os itens deste volume.",
      );
    } finally {
      volumeItemsRequestsRef.current.delete(volume.id);
    }
  };

  const volumesColumns = [
    { key: "name", label: "Volume" },
    { key: "code", label: "Código" },
    { key: "status", label: "Status" },
    {
      key: "hasShipment",
      label: "Carregamento",
      align: "center",
      render: (value, volume) =>
        volume.hasShipment ? (
          <Chip
            label={`#${volume.shipmentId}`}
            size="small"
            color="warning"
            icon={<FontAwesomeIcon icon={faCircleExclamation} />}
            sx={{ fontWeight: "bold" }}
          />
        ) : (
          <Chip label="Livre" size="small" color="success" variant="filled" />
        ),
    },
    { key: "created_at", label: "Criado Em", align: "center" },
  ];

  const volumesCollapseConfig = {
    key: ["items"],
    showToggleWhenEmpty: true,
    onExpand: handleLoadVolumeItems,
    renderEmpty: (volume) => {
      if (volume.itemsLoading || !volume.itemsLoaded) {
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Carregando itens do volume...
            </Typography>
          </Box>
        );
      }

      return (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          Nenhum item neste volume.
        </Typography>
      );
    },
    totalField: [],
    columns: [
      [
        { key: "items.name", label: "Item" },
        { key: "items.address", label: "Endereço" },
        { key: "items.location", label: "Localização" },
        {
          key: "items.quantity",
          label: "Quantidade",
          numeric: true,
          align: "center",
        },
        {
          key: "items.actions",
          label: "Ações",
          align: "center",
          render: (value, item, volume) => {
            const isLocked = volume.hasShipment;
            return (
              <Tooltip
                title={
                  isLocked
                    ? `Volume vinculado ao carregamento #${volume.shipmentId}`
                    : "Deletar item"
                }
                placement="top">
                <span>
                  <IconButton
                    color="error"
                    size="small"
                    disabled={isLocked}
                    onClick={() => handleDeleteVolumeItem(volume, item)}>
                    <FontAwesomeIcon icon={faTrash} />
                  </IconButton>
                </span>
              </Tooltip>
            );
          },
        },
      ],
    ],
  };

  useEffect(() => {
    if (!movementDetailsResponseData) return;

    const type = movementDetailsResponseData?.movement_type?.name; // String vinda do Accessor Laravel

    // Configuração padrão de colunas comuns
    const commonEquip = [
      { key: "equipment", label: "Descrição" },
      { key: "contract_item", label: "Item do Contrato" },
      { key: "status", label: "Status" },
    ];

    switch (type) {
      case "ALUGUEL":
      case "RESERVA":
        setEquipColumns([...commonEquip]);
        // setProdColumns([
        //   { key: "product", label: "Insumo", width: "40%" },
        //   { key: "quantity_product", label: "Quantidade" },
        //   { key: "department", label: "Departamento" },
        // ]);
        break;

      case "DEVOLUÇÃO":
        setEquipColumns([...commonEquip]);
        // setProdColumns([
        //   { key: "product", label: "Insumo Retornado" },
        //   { key: "quantity_product", label: "Qtd. Retornada" },
        // ]);
        break;

      case "SUBSTITUIÇÃO":
        setEquipColumns([...commonEquip]);
        setEquipmentsCollapseConfig({
          key: ["return_equipment"],
          titles: ["Equipamento para retorno"],
          totalField: [],
          columns: [
            [
              { key: "return_equipment.number", label: "Tombo" },
              { key: "movementsEquipments.name", label: "Descrição" },
              {
                key: "status_icon",
                label: "Status",
                align: "center",
                render: (value, item) => {
                  // "item" aqui refere-se ao objeto dentro de return_equipment
                  const status = item?.status;
                  const shipment_item_status = item?.shipment_item_status;

                  if (
                    status === "TOTALMENTE ATENDIDA" &&
                    (shipment_item_status ===
                      ShipmentItemStatus.AGUARDANDO_CONFIRMACAO ||
                      !shipment_item_status)
                  ) {
                    return (
                      <Box title="Pendente" sx={{ color: "warning.main" }}>
                        <FontAwesomeIcon icon={faCircleExclamation} size="xl" />
                      </Box>
                    );
                  }

                  if (status === "CANCELADO") {
                    return (
                      <Box title="Item Cancelado" sx={{ color: "error.main" }}>
                        <FontAwesomeIcon icon={faCircleChevronLeft} size="xl" />
                      </Box>
                    );
                  }

                  if (
                    status === "TOTALMENTE ATENDIDA" &&
                    shipment_item_status !==
                      ShipmentItemStatus.AGUARDANDO_CONFIRMACAO
                  ) {
                    return (
                      <Box title="Atendido" sx={{ color: "success.main" }}>
                        <FontAwesomeIcon
                          icon={faCircleChevronRight}
                          size="xl"
                        />
                      </Box>
                    );
                  }

                  // Ícone padrão ou nulo se não houver status correspondente
                  return null;
                },
              },
            ],
          ],
        });
        // setProdColumns([
        //   { key: "product", label: "Insumo Retornado" },
        //   { key: "quantity_product", label: "Qtd. Retornada" },
        // ]);
        break;
      case "AVULSO":
        setEquipColumns([
          { key: "equipment", label: "Equipamento", width: "40%" },
          { key: "status", label: "Status" },
        ]);
        break;

      default:
        break;
    }
  }, [movementDetailsResponseData]);

  // * Usado para editar ou não volumes
  const isTotalAttended =
    movementDetailsResponseData?.movement_type?.id === MovementEnum.SUBSTITUICAO
      ? movementDetailsResponseData?.service_order?.status_id ===
        GenericStatusEnum.FULLY_ATTENDED
      : movementDetailsResponseData?.status_id ===
        GenericStatusEnum.FULLY_ATTENDED;

  // * Usado para adicionar ou não itens no movimento
  const isTotalAttendedOrder =
    movementDetailsResponseData?.service_order?.status_id ===
    GenericStatusEnum.FULLY_ATTENDED;

  const isReplacement =
    formData?.movement_type_id === MovementEnum.SUBSTITUICAO;

  // 2. Adicione o estado para o modal (dentro do componente)
  const [isEditVolumeModalOpen, setIsEditVolumeModalOpen] = useState(false);
  const [isLoadingEditVolumes, setIsLoadingEditVolumes] = useState(false);
  const [editVolumeData, setEditVolumeData] = useState({
    bipeds: {},
    initialVolumes: [],
  });

  // 3. Função para abrir o modal
  const handleOpenEditVolumes = async () => {
    if (isLoadingEditVolumes) return;

    setIsLoadingEditVolumes(true);

    try {
      // A listagem da tabela é resumida; para editar precisamos dos itens
      // de cada volume, então buscamos a versão completa sob demanda.
      const response = await baseServiceGet(
        entities.movements.volumes.get(movementId),
        {
          page: paginationVolumes.page,
          perPage: paginationVolumes.perPage,
          sort: { column: "id", order: "desc" },
          lean: true,
        },
      );
      const volumesWithItems = (response?.result?.data ?? []).map(
        mapVolumeRecord,
      );

      setVolumeItemsState((prev) => {
        const next = { ...prev };

        volumesWithItems.forEach((volume) => {
          next[volume.id] = { items: volume.items, loading: false };
        });

        return next;
      });

      const { bipeds, initialVolumes } = mapMovementDataToVolumeForm(
        movementsEquipments,
        movementsProducts,
        volumesWithItems,
      );
      setEditVolumeData({ bipeds, initialVolumes });
      setIsEditVolumeModalOpen(true);
    } catch (error) {
      console.error("Erro ao carregar volumes para edição:", error);
      showNotification(
        "error",
        "Não foi possível carregar os volumes para edição.",
      );
    } finally {
      setIsLoadingEditVolumes(false);
    }
  };

  const hasAnyEditableVolume = movementsVolumes.some((v) => !v.hasShipment);
  const isInitialDataLoading =
    isLoadingMovementDetails ||
    isLoadingMovementItemEquipments ||
    isLoadingMovementItemProducts ||
    isLoadingMovementVolumes;

  async function handleEditVolumes(volumes) {
    try {
      await updateVolumeFromMovement.mutateAsync({
        movementId,
        volumes,
      });
    } catch (error) {
      console.error("Erro ao atualizar volumes:", {
        status: error.status,
        message: error.message,
      });
    }
  }

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title={`Detalhes do Movimento`}
        showBackButton={true}
        backUrl="/aluguel/movimentos"
      />

      {isInitialDataLoading ? (
        <Box
          role="status"
          aria-live="polite"
          sx={{
            minHeight: "40vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            color: "text.secondary",
          }}>
          <CircularProgress aria-label="Carregando" />
          <Typography>Carregando informações do movimento...</Typography>
        </Box>
      ) : (
      <div className="container-fluid p-1">
        {/* Renderiza os detalhes do movimento principal */}
        <DetailsSectionRenderer
          sections={detailsMovementFields}
          formData={formData}
          readOnly
        />

        {/* Cabeçalho da lista de equipamentos do movimento com botão "Adicionar Equipamento" */}
        <ListHeader
          title="Listas de Itens do Movimento"
          buttonText="Adicionar Item"
          buttonLink={`/aluguel/movimentos/${movementId}/itens/${formData.service_order_id}/adicionar`}
          canAccess={canAccess}
          buttonIsNotVisible={isTotalAttendedOrder}
          permission="Criar itens de movimentos"
          extraButtons={[
            {
              text: "Carregamentos",
              link: `/aluguel/carregamentos?movement_id=${movementId}`,
              permission: "Ver carregamentos",
            },
          ]}
        />

        <Alert
          severity={isTotalAttendedOrder ? "success" : "warning"}
          sx={{ my: 2 }}>
          {isTotalAttendedOrder
            ? "Todos os itens já foram incluídos no movimento."
            : "Ainda restam itens para incluir no movimento."}
        </Alert>

        <Box sx={{ my: 2 }}>
          <hr />
        </Box>

        {/* Tabela dinâmica para exibir os equipamentos do movimento */}
        <div className="flex flex-column gap-2">
          <ListHeader
            title="Equipamentos"
            canAccess={canAccess}
            buttonIsNotVisible={true}
          />
          <TableWithInactiveFilter
            data={movementsEquipments}
            columns={equipColumns}
            collapseConfig={isReplacement ? equipmentsCollapseConfig : null}
            actions={actions}
            totalPages={totalPages}
            currentPage={pagination.page - 1}
            onPageChange={changePage}
            rowsPerPage={pagination.perPage}
            onRowsPerPageChange={(event) => {
              const newPerPage = +event.target.value;
              setPagination({ page: 1, perPage: newPerPage });
            }}
            filterValues={localFilterValues}
            localFilterValues={localFilterValues}
            applyUrlFilters={applyUrlFilters}
            type={isReplacement ? "collapse" : "default"}
            rowColorConfig={{
              colorMap: genericStatusColorMap,
              propertyKey: "status",
            }}
            showFilterInactive={false}
            defaultCollapseOpen={isReplacement ? true : false}
            isLoading={isLoadingMovementItemEquipments}
            isFetching={isFetchingMovementItemEquipments}
          />
        </div>

        <Box className="flex flex-column gap-2" sx={{ my: 2 }}>
          <ListHeader
            title="Volumes"
            buttonText={
              isLoadingEditVolumes ? "Carregando volumes..." : "Editar Volumes"
            }
            permission={"Atualizar volumes"}
            onClickButton={handleOpenEditVolumes}
            buttonIsNotVisible={isTotalAttended} // * A lógica é invertida
            canAccess={canAccess}
            buttonDisabled={isLoadingEditVolumes || !hasAnyEditableVolume} // * A lógica é invertida
          />
          <TableWithInactiveFilter
            data={volumesTableData}
            columns={volumesColumns}
            actions={actionsVolume}
            totalPages={totalPagesVolumes}
            currentPage={paginationVolumes.page - 1}
            onPageChange={changePageVolumes}
            rowsPerPage={paginationVolumes.perPage}
            onRowsPerPageChange={(event) => {
              const newPerPage = +event.target.value;
              setPaginationVolumes({ page: 1, perPage: newPerPage });
            }}
            filterValues={localFilterValuesVolumes}
            localFilterValues={localFilterValuesVolumes}
            applyUrlFilters={applyUrlFiltersVolumes}
            collapseConfig={volumesCollapseConfig}
            showFilterInactive={false}
            showActions={true}
            rowColorConfig={{
              colorMap: genericStatusColorMap,
              propertyKey: "status",
            }}
            isLoading={isLoadingMovementVolumes}
            isFetching={isFetchingMovementVolumes}
          />

          <EditVolumeModal
            open={isEditVolumeModalOpen}
            onClose={() => setIsEditVolumeModalOpen(false)}
            bipeds={editVolumeData.bipeds}
            initialVolumes={editVolumeData.initialVolumes}
            isSaving={false}
            onSave={(volumes) => handleEditVolumes(volumes)}
          />
        </Box>

        <div className="flex flex-column gap-2">
          <ListHeader
            title="Insumos"
            canAccess={canAccess}
            buttonIsNotVisible={true}
          />
          <TableWithInactiveFilter
            data={movementsProducts}
            columns={productsColumns}
            actions={actions}
            totalPages={totalPagesProducts}
            currentPage={paginationProducts.page - 1}
            onPageChange={changePageProducts}
            rowsPerPage={paginationProducts.perPage}
            onRowsPerPageChange={(event) => {
              const newPerPage = +event.target.value;
              setPaginationProducts({ page: 1, perPage: newPerPage });
            }}
            filterValues={localFilterValuesProducts}
            localFilterValues={localFilterValuesProducts}
            applyUrlFilters={applyUrlFiltersProducts}
            type="default"
            showFilterInactive={false}
            rowColorConfig={{
              colorMap: genericStatusColorMap,
              propertyKey: "status",
            }}
            isLoading={isLoadingMovementItemProducts}
            isFetching={isFetchingMovementItemProducts}
          />
        </div>

        {/* Botão de voltar para a lista de movimentos */}
        <div className="mt-3 d-flex gap-2">
          <Button
            type="button"
            text="Voltar"
            className="btn btn-blue-light fw-semibold"
            onClick={() => navigate(-1)}
          />
        </div>
      </div>
      )}

      {/* Modal de confirmação para ações de exclusão/ativação */}
      <ConfirmationModal
        open={openModalConfirmation}
        onClose={handleCancelConfirmation}
        onConfirm={handleConfirmAction}
        itemName={selectedItem?.product || selectedItem?.equipment || ""}
        text={action.text}
      />
    </MainLayout>
  );
};

export default DetailsMovementPage;
